import { ModuleWrapper, FragmentA } from "components/fragments";
import { HeaderB } from "components/headers";
import React, { useCallback, useContext, useMemo } from "react";
import lang from "translations";
import { Path } from "paths";
import { useModal, useRouter, useMount, useApi, useFilter } from "hooks";
import { Panel, Title, Button, DataTable, Icon, Text, Pill } from "components/commons";
import { VenueContext } from "contexts";
import { getTaxes, createTax, editTax } from "apis/tax.api";
import { AccountName, PillType, StyleType } from "enums";
import { taxesList, taxRequest, createTaxRequest, taxListFilterRequest } from "mappers";
import TaxFormModal from "../tax-form/tax-form.modal";
import { mixpanel, TrackEvent } from "mixpanel";
import { getMewsIntegrationStatus } from "apis/integration.api";
import { getAccountMappings } from "apis";
import { mapObject } from "services";
import useFilterStore, { filterName } from "hooks/filterStore";
import { isEmpty } from "lodash";
import { taxFilterState } from "./filters";
import { isOdooTaxLocked } from "services/venue.service";

const TaxSetting = () => {
  const { venue } = useContext(VenueContext);
  const { venueId } = venue;
  const taxLocked = isOdooTaxLocked(venue);
  const { history } = useRouter();
  const taxFormModal = useModal();
  const { setState: setFilterState, getState } = useFilterStore();

  const { modifyFilters, filterState, requestState } = useFilter(
    filterName.tax === getState().name && !isEmpty(getState().filter)
      ? getState().filter
      : taxFilterState(venueId),
    taxFilterState(venueId),
  );

  const {
    request: searchTaxRequest,
    loading,
    mappedData,
    error,
    result: searchTaxResult = { data: [], metadata: { total: 0 } },
  } = useApi({
    api: getTaxes,
    mapper: taxesList,
    isArray: true,
  });

  const fetchTaxes = useCallback(
    async (requestState) => {
      await searchTaxRequest(mapObject(requestState, taxListFilterRequest));
    },
    [searchTaxRequest],
  );

  const fetchUpdateStore = useCallback(
    async (requestState, filterState) => {
      setFilterState({
        name: filterName.tax,
        filter: filterState,
      });
      await fetchTaxes(requestState);
    },
    [fetchTaxes, setFilterState],
  );

  const refreshTaxes = useCallback(async () => {
    await fetchTaxes(requestState);
  }, [fetchTaxes, requestState]);

  const { request: mewsRequest, result } = useApi({
    api: getMewsIntegrationStatus,
    params: {
      venueId: venue?.venueId,
    },
    handleOwnError: true,
  });

  const { request: accountingCategoriesRequest, result: defaultAccountMapping } = useApi({
    api: getAccountMappings,
    params: {
      businessId: venue?.venueId,
    },
    handleOwnError: true,
  });

  const defaultTaxAccountMapping = defaultAccountMapping
    ? defaultAccountMapping?.data?.find(
        (d) => d.itemType === AccountName.TaxOnSalesLiabilityAccount,
      )
    : null;
  const defaultTaxAccountCodeId = defaultTaxAccountMapping
    ? defaultTaxAccountMapping?.accountCodeId
    : null;

  useMount(async () => {
    if (filterName.tax !== getState().name)
      setFilterState({
        name: filterName.tax,
        filter: {},
      });
    await fetchTaxes(requestState);
    await mewsRequest();
    accountingCategoriesRequest();
    mixpanel.track(TrackEvent.VisitedPage, {
      Page: lang.taxSettings,
    });
  });

  const isMewsAccountingIntegrated = Boolean(
    result?.data
      ?.flatMap((dt) => dt.integrationType)
      .join(",")
      ?.includes("MEWS_ACCOUNTING"),
  );

  const { request: createTaxRequestApi, loading: createTaxLoading } = useApi({
    api: createTax,
    params: {
      venueId,
    },
    paramsMapper: createTaxRequest,
    handleOwnError: {
      badrequest: true,
    },
  });

  const { request: editTaxRequestApi, loading: editTaxLoading } = useApi({
    api: editTax,
    params: {
      venueId,
    },
    paramsMapper: taxRequest,
    handleOwnError: {
      badrequest: true,
    },
  });

  const onSave = useCallback(
    async ({ api, message }, fields, success, error) => {
      try {
        const result = await api(fields);
        await refreshTaxes();
        return success(result, { message });
      } catch (e) {
        return error(e);
      }
    },
    [refreshTaxes],
  );

  const onCreateSave = useCallback(
    (fields, success, error) => {
      return onSave(
        { api: createTaxRequestApi, message: lang.populate(lang.taxAddedName, [fields.name]) },
        fields,
        success,
        error,
      );
    },
    [createTaxRequestApi, onSave],
  );

  const onEditSave = useCallback(
    (fields, success, error) => {
      return onSave({ api: editTaxRequestApi, message: lang.changesSaved }, fields, success, error);
    },
    [editTaxRequestApi, onSave],
  );

  const submitting = createTaxLoading || editTaxLoading;

  const data = useMemo(() => {
    return mappedData.map((d) => {
      const isEditDisabled = d.editable === false;

      return {
        ...d,
        nameAndTax: (
          <div className="flex items-center">
            <Text className="mr-sm" breakAll>
              {d.nameAndTax}
            </Text>
            {d.isDefault && (
              <Pill size="text-xs" type={PillType.Green}>
                {lang.default}
              </Pill>
            )}
          </div>
        ),
        edit: (
          <div className="display-hover">
            <Icon
              name="pencil"
              className={isEditDisabled ? "text-gray-light cursor-not-allowed" : "text-blue cursor-pointer"}
              onClick={() => {
                if (isEditDisabled) {
                  return;
                }

                taxFormModal.show({
                  title: lang.editTax,
                  primaryText: lang.save,
                  isEdit: true,
                  save: onEditSave,
                  id: d.id,
                  initialState: {
                    name: d.name,
                    percentage: d.percentage,
                    isDefault: d.isDefault,
                    id: d.id,
                    accountCodeId: d.accountCodeId ?? defaultTaxAccountCodeId,
                    isMewsTaxCode: d.isMewsTaxCode,
                    taxCode: d.taxCode,
                  },
                  isMewsTaxCode: d.isMewsTaxCode,
                  isMewsAccountingIntegrated,
                  defaultTaxAccountMapping,
                });
              }}
            />
          </div>
        ),
      };
    });
  }, [
    mappedData,
    taxFormModal,
    onEditSave,
    isMewsAccountingIntegrated,
    defaultTaxAccountMapping,
    defaultTaxAccountCodeId,
  ]);
  return (
    <ModuleWrapper
      error={error}
      header={
        <HeaderB
          returnText={lang.settings}
          title={lang.taxes}
          returnPath={Path.SETTING}
          onClick={() => history.push(Path.SETTING)}
        />
      }
    >
      <FragmentA title={""} description={lang.manageHowVenueTax}>
        <Panel>
          <Title md className="mb-sm">
            {lang.taxes}
          </Title>
          <DataTable
            page={filterState.page}
            pageSize={filterState.pageSize}
            onChangePage={modifyFilters}
            fetchList={fetchUpdateStore}
            total={searchTaxResult?.metadata?.total || 0}
            loading={loading}
            minWidth={0}
            columns={[
              {
                key: "nameAndTax",
                className: "py-lg px-6",
              },
              {
                key: "edit",
                className: "text-right p-lg",
              },
            ]}
            data={data}
            error={error}
            showHeader={false}
          />
          {!taxLocked && (
            <Button
              className="mt-md"
              type={StyleType.Secondary}
              onClick={() => {
                taxFormModal.show({
                  title: lang.addNewTax,
                  primaryText: lang.add,
                  initialState: {
                    name: undefined,
                    percentage: undefined,
                    isDefault: false,
                    id: 0,
                    accountCodeId: defaultTaxAccountCodeId,
                    taxCode: undefined,
                  },
                  isEdit: false,
                  save: onCreateSave,
                  isMewsAccountingIntegrated,
                  defaultTaxAccountMapping,
                });
              }}
            >
              {lang.addNewTax}
            </Button>
          )}
        </Panel>
      </FragmentA>
      <TaxFormModal
        {...taxFormModal}
        fetchingTax={loading}
        submitting={submitting}
        refreshList={refreshTaxes}
      />
    </ModuleWrapper>
  );
};

export default TaxSetting;
